import { AfterViewChecked, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl, Validators, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { tap, takeUntil, filter, take, map } from 'rxjs/operators';

import { PackageUser } from 'src/app/features/package-users';
import { SelectOption } from 'src/app/features/shared';
import { EMAIL_REGEX, EXTENDED_ASCII_REGEX } from 'src/app/features/shared/constants';
import { AbstractWizardStepComponent, CheckInTaskCode, WizardStep } from 'src/app/features/wizard';
import { RootStoreState } from 'src/app/store';

import { SignerDetailsSelectors } from '../../store/selectors';
import { SignerDetailsActions } from '../../store/actions';
import { SignerDetailsStatus, SignerDetailsSubmission } from '../../models/signer-details.model';

export const validationMessages: Record<string, Record<string, string>> = {
  firstName: {
    empty: 'Please enter your first name',
    invalid: 'Your first name must be 128 characters or less',
    invalidFormat: 'Your first name cannot contain non-standard characters',
  },
  lastName: {
    empty: 'Please enter your last name',
    invalid: 'Your last name must be 128 characters or less',
    invalidFormat: 'Your last name cannot contain non-standard characters',
  },
  emailAddress: {
    empty: 'Please enter your email address',
    invalid: 'Please enter your email address like: yourname@example.com',
    invalidFormat: 'Please enter your email address like: yourname@example.com',
  },
  phoneNumber: {
    empty: 'Please enter your phone number',
    invalid: 'Please enter your phone number like: (xxx) xxx-xxxx',
    invalidFormat: 'Please enter your phone number like: (xxx) xxx-xxxx',
  },
  dateOfBirth: {
    empty: 'Please enter either your date of birth OR the last 4 of your SSN',
    invalid: 'Please enter a valid date of birth',
    invalidFormat: 'Please enter a valid date of birth',
    age: 'You must be 16 or older',
  },
  ssn: {
    empty: 'Please enter either your date of birth OR the last 4 of your SSN',
    invalid: 'Please enter exactly 4 digits',
    invalidFormat: 'Please enter exactly 4 digits',
  },
  address1: {
    empty: 'Please enter your street address',
    invalid: 'Your street address must be 100 characters or less',
  },
  city: {
    empty: 'Please enter your city',
  },
  state: {
    empty: 'Select your state',
  },
  zipCode: {
    empty: 'Enter zip code',
    invalid: 'Invalid zip code',
    invalidFormat: 'Must be exactly 5 digits',
  },
};

const serviceErrorMap: Record<string, string> = {
  FirstName: 'firstName',
  LastName: 'lastName',
  Email: 'emailAddress',
  PhoneNumber: 'phoneNumber',
  DateOfBirth: 'dateOfBirth',
  Ssn: 'ssn',
  'ParticipantAddress.StreetAddress1': 'address1',
  'ParticipantAddress.City': 'city',
  'ParticipantAddress.StateCode': 'state',
  'ParticipantAddress.ZipCode': 'zipCode',
};

const serviceErrorCodeMap: Record<string, string> = {
  ERRREQUIRED: 'empty',
  ERRINVALID: 'invalid',
  ERRINVALIDAGE: 'age',
};

type ErrorMapping = Record<string, string>;

@Component({
    selector: 'app-signer-details',
    templateUrl: './signer-details-step.component.html',
    styleUrls: ['./signer-details-step.component.scss'],
    providers: [
        {
            provide: AbstractWizardStepComponent,
            useExisting: SignerDetailsStepComponent,
        },
    ],
    standalone: false
})
export class SignerDetailsStepComponent
  extends AbstractWizardStepComponent
  implements AfterViewChecked, OnDestroy, OnInit
{
  @Input() user: PackageUser;

  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  signatureName: FormControl<string | null>;
  emailAddress: UntypedFormControl;
  phoneNumber: UntypedFormControl;
  dateOfBirth: FormControl<string | null>;
  ssn: UntypedFormControl;
  address1: UntypedFormControl;
  address2: UntypedFormControl;
  city: UntypedFormControl;
  state: UntypedFormControl;
  zipCode: UntypedFormControl;

  submitAttempted = false;
  isFormValid = true; // Initially true to hide error messages
  jumpToError = false;

  signerDetailsStatus: SignerDetailsStatus;
  states$: Observable<SelectOption[]>;
  errors: ErrorMapping = {};

  private readonly destroySubject: Subject<boolean> = new Subject<boolean>();

  constructor(private readonly store: Store<RootStoreState.State>) {
    super(store);

    this.stepMetadata = {
      stepName: 'Additional Details Required',
      checkInTaskCode: CheckInTaskCode.SignerDetails,
    } as WizardStep;

    this.firstName = new FormControl('', [
      Validators.required,
      Validators.pattern(EXTENDED_ASCII_REGEX),
    ]);
    this.lastName = new FormControl('', [
      Validators.required,
      Validators.pattern(EXTENDED_ASCII_REGEX),
    ]);
    this.signatureName = new FormControl('');
    this.signatureName.disable();
    this.emailAddress = new UntypedFormControl('', [
      Validators.required,
      Validators.pattern(EMAIL_REGEX),
    ]);
    this.phoneNumber = new UntypedFormControl('', [Validators.required]);
    this.dateOfBirth = new UntypedFormControl('', [Validators.pattern(/^\d{8}$/)]);
    this.ssn = new UntypedFormControl('', [Validators.pattern(/^\d{4}$/)]);
    this.address1 = new UntypedFormControl('', [Validators.required]);
    this.address2 = new UntypedFormControl();
    this.city = new UntypedFormControl('', [Validators.required]);
    this.state = new UntypedFormControl('', [Validators.required]);
    this.zipCode = new UntypedFormControl('', [Validators.required]);

    this.formStep = new UntypedFormGroup({
      firstName: this.firstName,
      lastName: this.lastName,
      emailAddress: this.emailAddress,
      phoneNumber: this.phoneNumber,
      dateOfBirth: this.dateOfBirth,
      ssn: this.ssn,
      address1: this.address1,
      address2: this.address2,
      city: this.city,
      state: this.state,
      zipCode: this.zipCode,
    });
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.firstName.valueChanges
      .pipe(
        takeUntil(this.destroySubject),
        tap(() => this.autoSetSignature())
      )
      .subscribe();

    this.lastName.valueChanges
      .pipe(
        takeUntil(this.destroySubject),
        tap(() => this.autoSetSignature())
      )
      .subscribe();

    this.dateOfBirth.valueChanges
      .pipe(
        takeUntil(this.destroySubject),
        tap(() => {
          if (!this.isFieldEmpty(this.dateOfBirth) && this.dateOfBirth.valid) {
            this.determineDateOfBirthValidity(this.parseDateOfBirth());
            this.determineSsnValidity();
          }
        })
      )
      .subscribe();

    this.ssn.valueChanges
      .pipe(
        takeUntil(this.destroySubject),
        tap(() => {
          if (!this.isFieldEmpty(this.ssn) && this.ssn.valid) {
            this.determineDateOfBirthValidity(this.parseDateOfBirth());
            this.determineSsnValidity();
          }
        })
      )
      .subscribe();
  }

  ngAfterViewChecked(): void {
    if (this.jumpToError) {
      const invalidFields = document.querySelectorAll('.mdc-text-field--invalid');

      if (invalidFields.length > 0) {
        invalidFields[0].scrollIntoView({ block: 'center' });
      }
      this.jumpToError = false;
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this.onStepHidden();
  }

  autoSetSignature() {
    this.signatureName.setValue(`${this.firstName.value} ${this.lastName.value}`);
  }

  onStepVisible(): void {
    this.store
      .select(SignerDetailsSelectors.getSignerDetailsStatus)
      .pipe(
        tap((status) => {
          this.signerDetailsStatus = status;

          this.resetValidators();
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe();

    this.store.dispatch(SignerDetailsActions.FetchStates());
    this.store.dispatch(SignerDetailsActions.LogAdditionalDetailsRequested());
    this.states$ = this.store.select(SignerDetailsSelectors.states).pipe(
      map((states) => {
        states.sort((a, b) => a.code.localeCompare(b.code));
        return states.map((state) => ({ value: state.code, label: state.code }));
      })
    );

    this.store
      .select(SignerDetailsSelectors.signerDetailsSubmissionErrors)
      .pipe(
        tap((errors) => {
          this.errors = errors.reduce((acc, val) => {
            const field = serviceErrorMap[val.field];
            let error = serviceErrorCodeMap[val.errorCode];
            if (!validationMessages[field].hasOwnProperty(error)) {
              error = 'invalid';
            }
            const control = this.formStep.get(field);
            control.setErrors({ [error]: validationMessages[field][error]})
            return {
              ...acc,
              [field]: validationMessages[field][error],
            };
          }, {});
          this.jumpToError = true;
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe();

    this.store
      .select(SignerDetailsSelectors.areSignerDetailsSubmitted)
      .pipe(
        filter((isSubmitted) => isSubmitted),
        take(1),
        tap(() => super.goToNextStep()),
        takeUntil(this.destroySubject)
      )
      .subscribe();
  }

  onStepHidden(): void {
    this.destroySubject.next(undefined);
    this.destroySubject.complete();
  }

  public goToNextStep(payload?: any): void {
    this.submitAttempted = true;
    this.validateStep();

    const dateOfBirth = this.parseDateOfBirth();
    this.determineDateOfBirthValidity(dateOfBirth);
    this.determineSsnValidity();

    if (this.formStep.valid) {
      this.submitData(dateOfBirth);
    } else {
      this.handleErrors();
    }
  }

  parseDateOfBirth(): Date | null {
    if (this.isFieldEmpty(this.dateOfBirth) || this.dateOfBirth.value?.length !== 8) {
      return null;
    }

    // Ensure date of birth is a valid date
    const day = parseInt(this.dateOfBirth.value.substring(2, 4), 10);
    const month = parseInt(this.dateOfBirth.value.substring(0, 2), 10) - 1;
    const year = parseInt(this.dateOfBirth.value.substring(4, 8), 10);
    const dateOfBirth = new Date(year, month, day);

    const isValidYear = year >= 1753 && year <= 9999;
    const isValidDate =
      isValidYear &&
      dateOfBirth.getDate() === day &&
      dateOfBirth.getMonth() === month &&
      dateOfBirth.getFullYear() === year;

    return isValidDate ? dateOfBirth : null;
  }

  private get isDateOfBirthOrSsnRequired() {
    return this.signerDetailsStatus.isMissingSsn && this.signerDetailsStatus.isMissingDateOfBirth;
  }

  determineDateOfBirthValidity(dateOfBirth: Date | null) {
    const isDateOfBirthRequired = this.isDateOfBirthOrSsnRequired && this.isFieldEmpty(this.ssn);
    const isDateOfBirthInvalid = !dateOfBirth && !this.isFieldEmpty(this.dateOfBirth);

    if (isDateOfBirthInvalid) {
      this.errors.dateOfBirth = validationMessages.dateOfBirth.invalid;
      this.dateOfBirth.setErrors({ invalid: validationMessages.dateOfBirth.invalid });
      this.dateOfBirth.markAsDirty();
    } else if (isDateOfBirthRequired && this.isFieldEmpty(this.dateOfBirth)) {
      this.errors.dateOfBirth = validationMessages.dateOfBirth.empty;
      this.dateOfBirth.setErrors({ required: validationMessages.dateOfBirth.empty });
      this.dateOfBirth.markAsDirty();
    } else if (this.isFieldEmpty(this.dateOfBirth)) {
      this.errors.dateOfBirth = null;
      this.dateOfBirth.reset();
    }
  }

  determineSsnValidity() {
    const isSsnRequired = this.isDateOfBirthOrSsnRequired && this.isFieldEmpty(this.dateOfBirth);

    if (isSsnRequired && this.isFieldEmpty(this.ssn)) {
      this.errors.ssn = validationMessages.dateOfBirth.ssn;
      this.ssn.setErrors({ required: validationMessages.ssn.empty });
      this.ssn.markAsDirty();
    } else if (this.isFieldEmpty(this.ssn)) {
      this.errors.ssn = null;
      this.ssn.reset();
    }
  }

  private resetValidators(): void {
    if (!this.signerDetailsStatus.isMissingFirstName) {
      this.clearValidators(['firstName']);
      this.firstName.reset({ value: this.user.firstName, disabled: true });
    }
    if (!this.signerDetailsStatus.isMissingLastName) {
      this.clearValidators(['lastName']);
      this.lastName.reset({ value: this.user.lastName, disabled: true });
    }

    if (!this.signerDetailsStatus.isMissingEmail) {
      this.clearValidators(['emailAddress']);
    }

    if (!this.signerDetailsStatus.isMissingDateOfBirth) {
      this.clearValidators(['dateOfBirth']);
    }

    if (!this.signerDetailsStatus.isMissingSsn) {
      this.clearValidators(['ssn']);
    }

    if (!this.signerDetailsStatus.isMissingPhoneNumber) {
      this.clearValidators(['phoneNumber']);
    }

    if (!this.signerDetailsStatus.isMissingAddress) {
      this.clearValidators(['address1', 'city', 'state', 'zipCode']);
    }
  }

  private isFieldEmpty(control: UntypedFormControl): boolean {
    return control.value === null || control.value.trim() === '';
  }

  private submitData(dateOfBirth: Date): void {
    const dataToSubmit: SignerDetailsSubmission = {};

    if (this.signerDetailsStatus.isMissingFirstName) {
      dataToSubmit.firstName = this.firstName.value;
    }

    if (this.signerDetailsStatus.isMissingLastName) {
      dataToSubmit.lastName = this.lastName.value;
    }

    if (this.signerDetailsStatus.isMissingEmail) {
      dataToSubmit.email = this.emailAddress.value;
    }

    if (this.signerDetailsStatus.isMissingPhoneNumber) {
      dataToSubmit.phoneNumber = this.phoneNumber.value;
    }

    if (this.signerDetailsStatus.isMissingDateOfBirth && !!dateOfBirth) {
      dataToSubmit.dateOfBirth = dateOfBirth.toISOString();
    }

    if (this.signerDetailsStatus.isMissingSsn && !this.isFieldEmpty(this.ssn)) {
      dataToSubmit.ssn = this.ssn.value;
    }

    if (this.signerDetailsStatus.isMissingAddress) {
      dataToSubmit.participantAddress = {
        streetAddress1: this.address1.value,
        streetAddress2: this.address2.value,
        city: this.city.value,
        stateCode: this.state.value,
        zipCode: this.zipCode.value,
      };
    }

    this.errors = {};
    this.store.dispatch(SignerDetailsActions.SubmitSignerDetails({ payload: dataToSubmit }));
  }

  private handleErrors(): void {
    this.isFormValid = false;
    this.jumpToError = true;
    this.errors = {};
    Object.keys(validationMessages).forEach((key) => {
      this.populateWithValidationMessage(key);
    });
  }

  private populateWithValidationMessage(fieldName: string): void {
    const control = this.formStep.get(fieldName);
    if (!control) {
      return;
    }

    if (!control.errors) {
      return;
    }

    control.markAsDirty();
    if (control.errors.required) {
      this.errors[fieldName] = validationMessages[fieldName].empty;
    }
    if (control.errors.invalid) {
      this.errors[fieldName] = validationMessages[fieldName].invalid;
    }
    if (control.errors.pattern || control.errors.mask) {
      this.errors[fieldName] = validationMessages[fieldName].invalidFormat;
    }
  }

  clearValidators(fields: string[]) {
    fields.forEach((f) => {
      this.formStep.get(f).clearValidators();
      this.formStep.get(f).reset();
    });
  }
}
